import React, { useState, useEffect } from 'react';
import { Eye, Plus, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import documentService from '../../services/documentService';

/**
 * ReferenceCards - Display 1-2 reference cards below assistant messages
 * Shows related docs with View and Add actions
 */
export default function ReferenceCards({ 
  userId, 
  focusId, 
  conversationFocusId, 
  currentDocId, 
  onView, 
  onAdd 
}) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCandidates = async () => {
      if (!userId) return;

      setLoading(true);
      const newCandidates = [];
      const effectiveFocusId = focusId || conversationFocusId;

      try {
        // Priority 1: Related docs by focus
        if (effectiveFocusId) {
          const { data: focusDocs } = await documentService.getRecentDocsByFocus(userId, effectiveFocusId, 2);
          if (focusDocs && focusDocs.length > 0) {
            focusDocs
              .filter(doc => doc.id !== currentDocId)
              .slice(0, 2)
              .forEach(doc => {
                newCandidates.push({
                  id: doc.id,
                  type: 'doc',
                  title: doc.title || 'Untitled',
                  reason: 'Matches your current focus',
                  content_json: doc.content_json
                });
              });
          }
        }

        // Fallback: Recent docs if we don't have 2 yet
        if (newCandidates.length < 2) {
          const { data: recentDocs } = await documentService.getRecentDocs(userId, {
            limit: 2,
            excludeId: currentDocId
          });
          if (recentDocs && recentDocs.length > 0) {
            recentDocs
              .filter(doc => !newCandidates.find(c => c.id === doc.id)) // Avoid duplicates
              .slice(0, 2 - newCandidates.length)
              .forEach(doc => {
                newCandidates.push({
                  id: doc.id,
                  type: 'doc',
                  title: doc.title || 'Untitled',
                  reason: 'Recently edited',
                  content_json: doc.content_json
                });
              });
          }
        }

        setCandidates(newCandidates.slice(0, 2)); // Max 2
      } catch (error) {
        console.error('Error loading reference candidates:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCandidates();
  }, [userId, focusId, conversationFocusId, currentDocId]);

  if (loading || candidates.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.3 }}
      className="flex flex-col gap-2 mt-3 mb-2"
    >
      {candidates.map((candidate) => (
        <div
          key={candidate.id}
          className="group flex items-center gap-3 px-4 py-3 bg-purple-50/50 hover:bg-purple-50 rounded-lg border border-purple-100 transition-all duration-200"
        >
          {/* Icon and Info */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 mt-0.5">
              <FileText size={16} className="text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-purple-900">Related doc:</span>
                <span className="text-xs font-semibold text-purple-700 truncate">{candidate.title}</span>
              </div>
              <p className="text-xs text-purple-600 mt-0.5">{candidate.reason}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={() => onView(candidate)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-purple-700 hover:text-purple-900 hover:bg-purple-100 rounded transition-colors"
              title="Preview"
            >
              <Eye size={14} />
              View
            </button>
            <button
              onClick={() => onAdd(candidate.id, candidate.type, candidate.content_json)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors"
              title="Add to doc"
            >
              <Plus size={14} />
              Add
            </button>
          </div>
        </div>
      ))}
    </motion.div>
  );
}



